import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MdRefresh, MdCheckCircle, MdCancel, MdDownload } from 'react-icons/md';
import API from '../api';
import { useLanguage } from '../i18n';

// Every booking payment in one place: MoMo prompts, MTN-code claims waiting for a check, and payments
// recorded by hand. Staff verify or reject the MTN-code claims here.
const STATUS = {
    successful: { key: 'payments.statusPaid', fb: 'Paid', bg: '#dcfce7', color: '#15803d' },
    verified: { key: 'payments.statusVerified', fb: 'Recorded', bg: '#e0f2fe', color: '#0369a1' },
    claimed: { key: 'payments.statusClaimed', fb: 'Needs verification', bg: '#fef3c7', color: '#b45309' },
    pending: { key: 'payments.statusPending', fb: 'Waiting for phone approval', bg: '#dbeafe', color: '#1d4ed8' },
    failed: { key: 'payments.statusFailed', fb: 'Failed', bg: '#fee2e2', color: '#b91c1c' },
    rejected: { key: 'payments.statusRejected', fb: 'Rejected', bg: '#f1f5f9', color: '#475569' },
};

const money = (n, cur) => `${cur || 'RWF'} ${Number(n || 0).toLocaleString()}`;
const when = p => { const v = p.paid_at || p.created_at; return v ? new Date(v).toLocaleString() : ''; };

export default function Payments() {
    const { t } = useLanguage();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(null);
    const [status, setStatus] = useState('');
    const [method, setMethod] = useState('');
    const [search, setSearch] = useState('');
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');

    const load = useCallback(async () => {
        setLoading(true); setError('');
        try { const { data } = await API.get('/spaces/payments'); setRows(data); }
        catch (e) { setError(e.response?.data?.error || t('payments.loadFailed', 'Could not load payments.')); }
        finally { setLoading(false); }
    }, []); // eslint-disable-line
    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        const timer = setInterval(() => { API.get('/spaces/payments').then(r => setRows(r.data)).catch(() => {}); }, 30000);
        return () => clearInterval(timer);
    }, []);

    const act = async (p, action) => {
        const q = action === 'verify'
            ? t('payments.confirmVerify', 'Confirm you saw {amount} (ID {ref}) on your MoMo statement?', { amount: money(p.amount, p.currency), ref: p.provider_ref })
            : t('payments.confirmReject', 'Reject this payment submission?');
        if (!window.confirm(q)) return;
        setBusy(p.id);
        try { await API.post(`/spaces/payments/${p.id}/${action}`); await load(); }
        catch (e) { alert(e.response?.data?.error || t('payments.actionFailed', 'Action failed.')); }
        finally { setBusy(null); }
    };

    const filtered = useMemo(() => rows.filter(p => {
        if (status && p.status !== status) return false;
        if (method && p.provider !== method && !(method === 'manual' && p.source === 'manual')) return false;
        const d = new Date(p.paid_at || p.created_at);
        if (from && d < new Date(from)) return false;
        if (to && d > new Date(to + 'T23:59:59')) return false;
        if (search) {
            const hay = `${p.guest_name} ${p.guest_email} ${p.booking_id} ${p.provider_ref || ''} ${p.phone || ''} ${p.space_name} ${p.invoice_no} ${p.receipt_no || ''}`.toLowerCase();
            if (!hay.includes(search.toLowerCase())) return false;
        }
        return true;
    }), [rows, status, method, search, from, to]);

    const sum = list => list.reduce((a, p) => a + p.amount, 0);
    const paid = filtered.filter(p => p.status === 'successful');
    const claimed = filtered.filter(p => p.status === 'claimed');
    const byMethod = {};
    paid.forEach(p => { byMethod[p.method] = (byMethod[p.method] || 0) + p.amount; });
    const cur = rows[0]?.currency || 'RWF';

    const exportCsv = () => {
        const head = ['Date', 'Receipt', 'Invoice', 'Booking', 'Guest', 'Email', 'Facility', 'Space', 'Method', 'Reference', 'Phone', 'Amount', 'Status'];
        const lines = filtered.map(p => [when(p), p.receipt_no || '', p.invoice_no, p.booking_id, p.guest_name, p.guest_email, p.building_name, p.space_name, p.method, p.provider_ref || '', p.phone || '', p.amount, p.status]
            .map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','));
        const blob = new Blob([[head.join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8' });
        const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'payments.csv'; a.click();
    };

    const card = (label, value, sub, color) => (
        <div className="col-6 col-lg-3"><div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '14px 16px', height: '100%' }}>
            <div style={{ fontSize: 12, color: '#64748b' }}>{label}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: color || '#0f172a' }}>{value}</div>
            {sub && <div style={{ fontSize: 12, color: '#94a3b8' }}>{sub}</div>}
        </div></div>
    );

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <h4 style={{ fontWeight: 800, margin: 0 }}>{t('payments.title', 'Payments')}</h4>
                <div className="d-flex gap-2">
                    <button className="btn btn-outline-secondary btn-sm" onClick={exportCsv}><MdDownload size={16} /> CSV</button>
                    <button className="btn btn-outline-primary btn-sm" onClick={load}><MdRefresh size={16} /> {t('payments.refresh', 'Refresh')}</button>
                </div>
            </div>
            {error && <div className="alert alert-danger">{error}</div>}

            <div className="row g-3 mb-3">
                {card(t('payments.collected', 'Collected'), money(sum(paid), cur), `${paid.length} ${t('payments.paymentsWord', 'payments')}`, '#15803d')}
                {card(t('payments.awaiting', 'Awaiting verification'), money(sum(claimed), cur), `${claimed.length} ${t('payments.submissions', 'submissions')}`, claimed.length ? '#b45309' : undefined)}
                {card(t('payments.waitingPhone', 'Waiting for phone approval'), filtered.filter(p => p.status === 'pending').length, null)}
                {card(t('payments.failedRejected', 'Failed / rejected'), filtered.filter(p => p.status === 'failed' || p.status === 'rejected').length, null)}
            </div>
            {Object.keys(byMethod).length > 0 && (
                <div style={{ fontSize: 13, color: '#475569', marginBottom: 12 }}>
                    {Object.entries(byMethod).map(([m, v]) => <span key={m} style={{ marginRight: 16 }}><strong>{m}:</strong> {money(v, cur)}</span>)}
                </div>
            )}

            <div className="row g-2 mb-3">
                <div className="col-md-3"><input className="form-control" placeholder={t('payments.search', 'Search guest, booking, reference...')} value={search} onChange={e => setSearch(e.target.value)} /></div>
                <div className="col-6 col-md-2"><select className="form-select" value={status} onChange={e => setStatus(e.target.value)}>
                    <option value="">{t('payments.allStatuses', 'All statuses')}</option>
                    {Object.entries(STATUS).map(([k, m]) => <option key={k} value={k}>{t(m.key, m.fb)}</option>)}
                </select></div>
                <div className="col-6 col-md-2"><select className="form-select" value={method} onChange={e => setMethod(e.target.value)}>
                    <option value="">{t('payments.allMethods', 'All methods')}</option>
                    <option value="mtn">MTN MoMo prompt</option><option value="airtel">Airtel Money prompt</option>
                    <option value="mtn_code">MTN code (dial)</option><option value="manual">{t('payments.manual', 'Recorded by manager')}</option>
                </select></div>
                <div className="col-6 col-md-2"><input type="date" className="form-control" value={from} onChange={e => setFrom(e.target.value)} /></div>
                <div className="col-6 col-md-2"><input type="date" className="form-control" value={to} onChange={e => setTo(e.target.value)} /></div>
            </div>

            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflowX: 'auto' }}>
                <table className="table table-hover mb-0" style={{ fontSize: 13 }}>
                    <thead><tr>
                        <th>{t('payments.colDate', 'Date')}</th><th>{t('payments.colGuest', 'Guest')}</th><th>{t('payments.colBooking', 'Booking')}</th>
                        <th>{t('payments.colMethod', 'Method')}</th><th>{t('payments.colReference', 'Reference')}</th>
                        <th style={{ textAlign: 'right' }}>{t('payments.colAmount', 'Amount')}</th><th>{t('payments.colStatus', 'Status')}</th><th />
                    </tr></thead>
                    <tbody>
                        {loading && <tr><td colSpan={8} className="text-center text-muted py-4">{t('payments.loading', 'Loading...')}</td></tr>}
                        {!loading && filtered.length === 0 && <tr><td colSpan={8} className="text-center text-muted py-4">{t('payments.none', 'No payments found.')}</td></tr>}
                        {filtered.map(p => {
                            const m = STATUS[p.status] || { key: null, fb: p.status, bg: '#f1f5f9', color: '#475569' };
                            return (
                                <tr key={p.id}>
                                    <td style={{ whiteSpace: 'nowrap' }}>{when(p)}</td>
                                    <td><div style={{ fontWeight: 600 }}>{p.guest_name}</div><div style={{ color: '#94a3b8', fontSize: 12 }}>{p.guest_email}</div></td>
                                    <td><div>#{p.booking_id} · {p.invoice_no}</div><div style={{ color: '#94a3b8', fontSize: 12 }}>{p.building_name} · {p.space_name}</div></td>
                                    <td>{p.method}</td>
                                    <td><div>{p.provider_ref || p.receipt_no || '—'}</div>{p.phone && <div style={{ color: '#94a3b8', fontSize: 12 }}>{p.phone}</div>}</td>
                                    <td style={{ textAlign: 'right', fontWeight: 700 }}>{money(p.amount, p.currency)}<div style={{ color: '#94a3b8', fontSize: 11, fontWeight: 400 }}>{t('payments.ofTotal', 'of {total}', { total: money(p.total_amount, p.currency) })}</div></td>
                                    <td><span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 12, background: m.bg, color: m.color, whiteSpace: 'nowrap' }}>{m.key ? t(m.key, m.fb) : m.fb}</span></td>
                                    <td style={{ whiteSpace: 'nowrap' }}>
                                        {p.status === 'claimed' && (
                                            <>
                                                <button className="btn btn-success btn-sm me-1" disabled={busy === p.id} onClick={() => act(p, 'verify')}><MdCheckCircle size={15} /> {t('payments.verify', 'Verify')}</button>
                                                <button className="btn btn-outline-danger btn-sm" disabled={busy === p.id} onClick={() => act(p, 'reject')}><MdCancel size={15} /></button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
